import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const FilterControls = ({ 
  filters, 
  onFiltersChange, 
  onExport,
  dateBasis,
  onDateBasisChange,
  className = '' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'processed', label: 'Successfully Processed' },
    { value: 'needs-review', label: 'Needs Review' },
    { value: 'error', label: 'Processing Errors' },
    { value: 'pending', label: 'Pending' }
  ];

  const confidenceOptions = [
    { value: 'all', label: 'All Confidence Levels' },
    { value: 'high', label: 'High Confidence (80%+)' },
    { value: 'medium', label: 'Medium Confidence (60-79%)' },
    { value: 'low', label: 'Low Confidence (<60%)' }
  ];

  const fileTypeOptions = [
    { value: 'all', label: 'All File Types' },
    { value: 'pdf', label: 'PDF Files' },
    { value: 'jpg', label: 'JPG Images' },
    { value: 'png', label: 'PNG Images' }
  ];

  const densityOptions = [
    { value: 'comfortable', label: 'Comfortable' },
    { value: 'compact', label: 'Compact' },
    { value: 'minimal', label: 'Minimal' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      status: 'all',
      confidence: 'all',
      fileType: 'all',
      dateRange: { from: '', to: '' },
      searchTerm: '',
      density: 'comfortable'
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.status !== 'all') count++;
    if (filters?.confidence !== 'all') count++;
    if (filters?.fileType !== 'all') count++;
    if (filters?.dateRange?.from || filters?.dateRange?.to) count++;
    if (filters?.searchTerm) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
        >
          <Icon name={isExpanded ? "ChevronDown" : "ChevronRight"} size={16} />
          <span>Filters & View Options</span>
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>

        <div className="flex items-center space-x-2">
          <div className="hidden md:flex items-center bg-muted rounded-md p-1">
            <button
              type="button"
              onClick={() => onDateBasisChange?.('created_at')}
              className={`px-3 py-1 text-xs font-medium rounded transition-all ${
                dateBasis === 'created_at'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Upload Date (Work Log)
            </button>
            <button
              type="button"
              onClick={() => onDateBasisChange?.('invoice_date')}
              className={`px-3 py-1 text-xs font-medium rounded transition-all ${
                dateBasis === 'invoice_date'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Invoice Date (Ledger)
            </button>
          </div>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              iconName="X"
              iconPosition="left"
            >
              Clear All
            </Button>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            iconName="Download"
            iconPosition="left"
          >
            Export
          </Button>
        </div>
      </div>
      {/* Filter Content */}
      {isExpanded && (
        <div className="p-4 space-y-6">
          <div className="md:hidden flex items-center bg-muted rounded-md p-1 w-fit">
            <button
              type="button"
              onClick={() => onDateBasisChange?.('created_at')}
              className={`px-3 py-1 text-xs font-medium rounded transition-all ${
                dateBasis === 'created_at'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Upload Date (Work Log)
            </button>
            <button
              type="button"
              onClick={() => onDateBasisChange?.('invoice_date')}
              className={`px-3 py-1 text-xs font-medium rounded transition-all ${
                dateBasis === 'invoice_date'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Invoice Date (Ledger)
            </button>
          </div>
          {/* Primary Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Select
              label="Upload Status"
              options={statusOptions}
              value={filters?.status || 'all'}
              onChange={(value) => handleFilterChange('status', value)}
              className="min-w-0"
            />

            <Select
              label="Confidence Level"
              options={confidenceOptions}
              value={filters?.confidence || 'all'}
              onChange={(value) => handleFilterChange('confidence', value)}
              className="min-w-0"
            />

            <Select
              label="File Type"
              options={fileTypeOptions}
              value={filters?.fileType || 'all'}
              onChange={(value) => handleFilterChange('fileType', value)}
              className="min-w-0"
            />

            <Select
              label="View Density"
              options={densityOptions}
              value={filters?.density || 'comfortable'}
              onChange={(value) => handleFilterChange('density', value)}
              className="min-w-0"
            />
          </div>

          {/* Date Range Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Date Range</label>
            <div className="flex items-center space-x-4">
              <Input
                type="date"
                placeholder="From date"
                value={filters?.dateRange?.from || ''}
                onChange={(e) => handleFilterChange('dateRange', {
                  ...filters?.dateRange,
                  from: e?.target?.value
                })}
                className="w-40"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                placeholder="To date"
                value={filters?.dateRange?.to || ''}
                onChange={(e) => handleFilterChange('dateRange', {
                  ...filters?.dateRange,
                  to: e?.target?.value
                })}
                className="w-40"
              />
            </div>
          </div>

          {/* Search Filter */}
          <div className="space-y-2">
            <Input
              type="search"
              label="Search Uploads"
              placeholder="Search by filename, vendor, or batch ID..."
              value={filters?.searchTerm || ''}
              onChange={(e) => handleFilterChange('searchTerm', e?.target?.value)}
              className="max-w-md"
            />
          </div>

          {/* Quick Filter Presets */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Quick Filters</label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleFilterChange('status', 'error')}
              >
                Show Errors Only
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleFilterChange('confidence', 'low')}
              >
                Low Confidence
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                  handleFilterChange('dateRange', {
                    from: lastWeek?.toISOString()?.split('T')?.[0],
                    to: today?.toISOString()?.split('T')?.[0]
                  });
                }}
              >
                Last 7 Days
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
                  handleFilterChange('dateRange', {
                    from: lastMonth?.toISOString()?.split('T')?.[0],
                    to: today?.toISOString()?.split('T')?.[0]
                  });
                }}
              >
                Last 30 Days
              </Button>
            </div>
          </div>

          {/* Active Filters Summary */}
          {activeFilterCount > 0 && (
            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} applied
                </span>
                <div className="flex flex-wrap gap-2">
                  {filters?.status !== 'all' && (
                    <span className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
                      <span>Status: {statusOptions?.find(o => o?.value === filters?.status)?.label}</span>
                      <button
                        onClick={() => handleFilterChange('status', 'all')}
                        className="hover:bg-primary/20 rounded p-0.5"
                      >
                        <Icon name="X" size={10} />
                      </button>
                    </span>
                  )}
                  {filters?.confidence !== 'all' && (
                    <span className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
                      <span>Confidence: {confidenceOptions?.find(o => o?.value === filters?.confidence)?.label}</span>
                      <button
                        onClick={() => handleFilterChange('confidence', 'all')}
                        className="hover:bg-primary/20 rounded p-0.5"
                      >
                        <Icon name="X" size={10} />
                      </button>
                    </span>
                  )}
                  {filters?.searchTerm && (
                    <span className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
                      <span>Search: "{filters?.searchTerm}"</span>
                      <button
                        onClick={() => handleFilterChange('searchTerm', '')}
                        className="hover:bg-primary/20 rounded p-0.5"
                      >
                        <Icon name="X" size={10} />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterControls;
